import type { ConstValues } from '@openx/types/helpers';
import type { TargetingFieldsValues } from '@openx/types/targeting/targeting';
import type { RequiredTargetingFormType } from '@openx/types/targeting/targetingPresets';
import type { SetStateAction } from 'react';
import type { TargetingPresetOptionProps } from '../utils/types';

export const RequiredTargetingSectionKeys = {
  FORMAT: 'format',
  CHANNEL: 'channel',
  DEVICE: 'device',
} as const;

export type RequiredTargetingSectionKeysType = ConstValues<typeof RequiredTargetingSectionKeys>;

export const TargetingElementKeys = {
  FORMAT_BANNER: 'BANNER',
  FORMAT_VIDEO: 'VIDEO',
  FORMAT_CTV: 'CTV',
  FORMAT_NATIVE: 'NATIVE',

  CHANNEL_WEB: 'WEB',
  CHANNEL_APP: 'APP',

  DEVICE_DESKTOP: 'desktop',
  DEVICE_MOBILE: 'mobile-category',
  DEVICE_MOBILE_SMARTPHONE: 'phone',
  DEVICE_MOBILE_TABLET: 'tablet',
  DEVICE_TV: 'tv-category',
  DEVICE_TV_CONNECTEDTV: 'tv',
  DEVICE_TV_SETTOPBOX: 'set-top-box',
} as const;

export type TargetingElementKeysType = ConstValues<typeof TargetingElementKeys>;

export type Element = {
  mainOption: TargetingPresetOptionProps;
  subOptions?: TargetingPresetOptionProps[];
};

export type Section = {
  isLoading: boolean;
  label: string;
  elements: Element[];
};

export type RequiredParamsType = {
  sections: Record<RequiredTargetingSectionKeysType, Section>;
};

export type SetRequiredParams = (
  updater: SetStateAction<RequiredParamsType>,
  options?: {
    /**
     * `silent: true` applies the state change without notifying `onTargetingUpdate`, so the host form value is left
     * untouched. Reserved for the initial hydration of a section (see `useSyncInitialTargeting`), which merely mirrors
     * the value the form already holds - emitting it would write a rebuilt (and therefore possibly re-serialised)
     * rendering context back into a pristine form and mark the package dirty without any user interaction.
     */
    silent?: boolean;
  }
) => void;

export type TargetingSettingsContextType = {
  getState: () => RequiredParamsType | null;
  setState: SetRequiredParams;
  subscribe: (listener: () => void) => () => void;
};

export type OnTargetingUpdateType = (event: TargetingFieldsValues, state: RequiredTargetingFormType) => void;

export type OnTargetingUpdateFn = (state: RequiredTargetingFormType) => void;
